import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Link2, Copy, Mail, MessageCircle, Pencil, XCircle, BadgeCheck, Plus, Save, Undo2, Image } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { StatusBadge } from "@/components/StatusBadge";
import { BookingDialog } from "@/components/BookingDialog";
import { GuestForm, emptyGuest, defaultGuests, applyScan } from "@/components/GuestForm";
import { api, errMsg, errList, fmtDate, openBlob } from "@/lib/api";
import { tr, LANGS } from "@/lib/i18n";

const t = tr("it");

function LinkDialog({ open, onOpenChange, booking }) {
  const [info, setInfo] = useState(null);
  const [lang, setLang] = useState(booking.lang || "it");
  useEffect(() => {
    if (open) api.get(`/bookings/${booking.id}/link`).then(({ data }) => setInfo(data)).catch((e) => toast.error(errMsg(e)));
  }, [open, booking.id]);
  const text = info?.texts?.[lang] || "";
  const copy = (s) => navigator.clipboard.writeText(s).then(() => toast.success("Copiato negli appunti")).catch(() => toast.error("Copia non riuscita: seleziona il testo e copialo a mano"));
  const sendMail = async () => {
    try {
      await api.post(`/bookings/${booking.id}/send-link`);
      toast.success(`Email inviata a ${booking.email}`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const phone = (booking.phone || "").replace(/[^\d]/g, "");
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="checkin-link-dialog">
        <DialogHeader><DialogTitle>Link check-in online</DialogTitle></DialogHeader>
        {info && (
          <div className="space-y-4 min-w-0">
            <div className="flex min-w-0 items-center gap-2 rounded-lg bg-stone-100 p-2">
              <code className="min-w-0 flex-1 truncate text-xs" data-testid="checkin-link-value">{info.link}</code>
              <Button size="sm" variant="ghost" onClick={() => copy(info.link)} data-testid="copy-link-button"><Copy className="h-4 w-4" /></Button>
            </div>
            <p className="text-xs text-stone-500">Valido fino al {fmtDate(info.expires)}</p>
            <Tabs value={lang} onValueChange={setLang}>
              <TabsList>{LANGS.map(([c]) => <TabsTrigger key={c} value={c} data-testid={`link-lang-${c}`}>{c.toUpperCase()}</TabsTrigger>)}</TabsList>
            </Tabs>
            <Textarea readOnly value={text} rows={5} data-testid="checkin-link-text" />
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={() => copy(text)} data-testid="copy-text-button"><Copy className="h-4 w-4 mr-2" />Copia testo</Button>
              <Button variant="outline" asChild data-testid="whatsapp-button">
                <a href={`https://wa.me/${phone}?text=${encodeURIComponent(text)}`} target="_blank" rel="noreferrer"><MessageCircle className="h-4 w-4 mr-2" />WhatsApp</a>
              </Button>
              <Button onClick={sendMail} disabled={!booking.email} data-testid="send-link-email-button"><Mail className="h-4 w-4 mr-2" />Invia email</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ConfirmDialog({ open, onOpenChange, onConfirm, guests }) {
  const [ok, setOk] = useState(false);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="confirm-checkin-dialog">
        <DialogHeader><DialogTitle>Conferma check-in (riconoscimento de visu)</DialogTitle></DialogHeader>
        <p className="text-sm text-stone-600">Verifica di persona i documenti originali di {guests.length} ospiti e la corrispondenza con i dati inseriti. Dopo la conferma le schedine diventano inviabili ad Alloggiati Web e le foto dei documenti vengono eliminate secondo la policy privacy.</p>
        <label className="flex items-start gap-3 text-sm">
          <Checkbox checked={ok} onCheckedChange={(v) => setOk(!!v)} data-testid="confirm-devisu-checkbox" />
          Ho identificato di persona tutti gli ospiti e verificato i documenti
        </label>
        <DialogFooter><Button disabled={!ok} onClick={onConfirm} data-testid="confirm-checkin-submit"><BadgeCheck className="h-4 w-4 mr-2" />Conferma check-in</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function BookingDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [b, setB] = useState(null);
  const [guests, setGuests] = useState([]);
  const [docs, setDocs] = useState([]);
  const [errors, setErrors] = useState([]);
  const [dlg, setDlg] = useState("");
  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/bookings/${id}`);
      setB(data);
      setGuests(data.guests.length ? data.guests : defaultGuests(data.guests_count));
    } catch (e) {
      toast.error(errMsg(e));
    }
  }, [id]);
  useEffect(() => {
    load();
    api.get("/lookup/documenti").then(({ data }) => setDocs(data));
  }, [load]);
  const onScan = async (i, file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/bookings/${id}/document`, fd);
      setGuests((gs) => gs.map((g, j) => (j === i ? applyScan(g, data) : g)));
      if (data.mrz_found) toast.success("Dati letti dal documento: controlla e salva");
      return data;
    } catch (e) {
      toast.error(errMsg(e));
      return null;
    }
  };
  if (!b) return <p className="text-stone-500">Caricamento…</p>;
  const locked = b.guests.some((g) => ["inviata", "ricevuta"].includes(g.schedina_status)) || b.status === "cancellata";
  const save = async () => {
    setErrors([]);
    try {
      await api.put(`/bookings/${id}/guests`, guests);
      toast.success("Dati ospiti salvati");
      load();
    } catch (e) {
      setErrors(errList(e));
      toast.error(errMsg(e));
    }
  };
  const act = async (path, msg) => {
    try {
      await api.post(`/bookings/${id}/${path}`);
      toast.success(msg);
      setDlg("");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const photos = b.guests.flatMap((g) => g.photo_ids || []);
  return (
    <div className="space-y-6" data-testid="booking-detail-page">
      <div className="rounded-2xl border bg-white p-5 sm:p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <StatusBadge kind="booking" status={b.status} testId="booking-status" />
            <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight mt-2" data-testid="booking-ref-name">{b.ref_name || "Senza nome"}</h1>
            <p className="text-stone-500 mt-1">{b.room_name} · {fmtDate(b.arrival)} → {fmtDate(b.departure)} · {b.guests_count} ospiti · {b.source}</p>
            {b.gdpr_accepted_at && <p className="text-xs text-stone-500 mt-1">Consenso privacy registrato il {new Date(b.gdpr_accepted_at).toLocaleString("it-IT")}</p>}
            {b.confirmed_at && <p className="text-xs text-emerald-700 mt-1">Confermato de visu da {b.confirmed_by} il {new Date(b.confirmed_at).toLocaleString("it-IT")}</p>}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => setDlg("link")} data-testid="open-link-dialog-button"><Link2 className="h-4 w-4 mr-2" />Link check-in</Button>
            <Button variant="outline" onClick={() => setDlg("edit")} disabled={b.status === "cancellata"} data-testid="edit-booking-button"><Pencil className="h-4 w-4 mr-2" />Modifica</Button>
            {b.status === "confermato" ? (
              <Button variant="outline" onClick={() => act("unconfirm", "Conferma annullata")} disabled={locked} data-testid="unconfirm-button"><Undo2 className="h-4 w-4 mr-2" />Annulla conferma</Button>
            ) : (
              <Button onClick={() => setDlg("confirm")} disabled={!b.guests.length || b.status === "cancellata"} data-testid="open-confirm-dialog-button"><BadgeCheck className="h-4 w-4 mr-2" />Conferma check-in</Button>
            )}
            {b.status !== "cancellata" && <Button variant="ghost" onClick={() => setDlg("cancel")} className="text-rose-700" data-testid="cancel-booking-button"><XCircle className="h-4 w-4 mr-2" />Cancella</Button>}
          </div>
        </div>
        {b.notes && <p className="mt-4 text-sm text-stone-600 bg-stone-50 rounded-lg p-3">{b.notes}</p>}
        {photos.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2">
            {photos.map((p, i) => <Button key={p} size="sm" variant="outline" onClick={() => openBlob(`/photos/${p}`).catch((e) => toast.error(errMsg(e)))} data-testid={`view-photo-${i}`}><Image className="h-4 w-4 mr-1" />Foto documento {i + 1}</Button>)}
          </div>
        )}
      </div>
      <div className="flex items-center justify-between">
        <h2 className="font-display text-2xl font-bold">Ospiti</h2>
        {!locked && <Button variant="outline" size="sm" onClick={() => setGuests([...guests, emptyGuest(guests.some((g) => g.tipo === "18") ? "20" : "19")])} data-testid="add-guest-button"><Plus className="h-4 w-4 mr-1" />Aggiungi</Button>}
      </div>
      {locked && <p className="text-sm text-stone-500" data-testid="guests-locked-note">Schedine già inviate: i dati non sono più modificabili.</p>}
      <GuestForm guests={guests} setGuests={setGuests} t={t} docs={docs} onScan={onScan} readOnly={locked} />
      {errors.length > 0 && (
        <ul className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800 list-disc pl-8" data-testid="guest-errors">{errors.map((e) => <li key={e}>{e}</li>)}</ul>
      )}
      {!locked && <div className="sticky bottom-20 lg:bottom-4 flex justify-end"><Button size="lg" onClick={save} className="shadow-lg" data-testid="save-guests-button"><Save className="h-4 w-4 mr-2" />Salva ospiti</Button></div>}
      <LinkDialog open={dlg === "link"} onOpenChange={(o) => setDlg(o ? "link" : "")} booking={b} />
      <BookingDialog open={dlg === "edit"} onOpenChange={(o) => setDlg(o ? "edit" : "")} booking={b} onSaved={load} />
      <ConfirmDialog open={dlg === "confirm"} onOpenChange={(o) => setDlg(o ? "confirm" : "")} guests={b.guests} onConfirm={() => act("confirm", "Check-in confermato: schedine pronte all'invio")} />
      <AlertDialog open={dlg === "cancel"} onOpenChange={(o) => setDlg(o ? "cancel" : "")}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancellare la prenotazione?</AlertDialogTitle>
            <AlertDialogDescription>Verrà esclusa da calendario e movimentazione ISTAT.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={async () => { await act("cancel", "Prenotazione cancellata"); nav("/prenotazioni"); }} data-testid="confirm-cancel-booking">Cancella</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
